import { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react'
import { X, CheckCircle, AlertTriangle, XCircle, Info, Loader2 } from 'lucide-react'
import { reportClientError } from '../lib/reportError'

// Error messages that look like production bugs (DB errors, API failures, schema issues)
// — not user-intent errors like "Name is required"
const PRODUCTION_ERROR_PATTERNS = [
  /column|schema.?cache|relation|PGRST/i,
  /could not find/i,
  /violates|constraint/i,
  /permission.?denied|not authorized/i,
  /failed to fetch|network error/i,
  /HTTP [45]\d{2}/i,
  /supabase|postgrest/i,
  /syntax error|unexpected token/i,
  /Cannot read|undefined is not/i,
  /timed? out|timeout/i,
]
function looksLikeProductionError(msg) {
  if (!msg || typeof msg !== 'string') return false
  return PRODUCTION_ERROR_PATTERNS.some(p => p.test(msg))
}

const ToastContext = createContext({})

let toastId = 0

const ICONS = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
  loading: Loader2,
}

const COLORS = {
  success: 'bg-green-50 border-green-300 text-green-800',
  error: 'bg-red-50 border-red-300 text-red-800',
  warning: 'bg-amber-50 border-amber-300 text-amber-800',
  info: 'bg-blue-50 border-blue-300 text-blue-800',
  loading: 'bg-navy-50 border-navy-200 text-navy-800',
}

const ICON_COLORS = {
  success: 'text-green-500',
  error: 'text-red-500',
  warning: 'text-amber-500',
  info: 'text-blue-500',
  loading: 'text-navy-500',
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timers = useRef({})

  const removeToast = useCallback((id) => {
    clearTimeout(timers.current[id])
    delete timers.current[id]
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const addToast = useCallback((message, type = 'info', duration = 4000, action = null) => {
    const id = ++toastId
    setToasts(prev => [...prev, { id, message, type, action }])
    if (duration > 0) {
      timers.current[id] = setTimeout(() => removeToast(id), duration)
    }
    // Report production-looking error toasts to the system monitor
    // so it can diagnose and fix them without Aaron having to report them manually.
    if (type === 'error' && looksLikeProductionError(String(message))) {
      reportClientError({
        error_message: String(message),
        error_type:    'toast_error',
        page_url:      window.location.href,
      })
    }
    return id
  }, [removeToast])

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}

      {/* Toast container — fixed top-right, above TopBar (z-30), below Modal (z-50) */}
      {toasts.length > 0 && (
        <div className="fixed top-4 right-4 z-40 flex flex-col gap-2 max-w-sm w-full pointer-events-none">
          {toasts.map(t => {
            const Icon = ICONS[t.type]
            return (
              <div
                key={t.id}
                className={`pointer-events-auto flex items-start gap-2 px-3 py-2.5 rounded-lg border shadow-lg text-sm animate-slide-in ${COLORS[t.type]}`}
              >
                <Icon size={18} className={`mt-0.5 shrink-0 ${ICON_COLORS[t.type]} ${t.type === 'loading' ? 'animate-spin' : ''}`} />
                <span className="flex-1 leading-snug">{t.message}</span>
                {t.action && (
                  <button
                    onClick={() => { t.action.onClick(); removeToast(t.id) }}
                    className="shrink-0 ml-1 px-2 py-0.5 rounded text-xs font-semibold bg-white/60 hover:bg-white/90 border border-current/20 transition-colors"
                  >
                    {t.action.label}
                  </button>
                )}
                <button
                  onClick={() => removeToast(t.id)}
                  className="shrink-0 mt-0.5 opacity-60 hover:opacity-100 transition-opacity"
                >
                  <X size={14} />
                </button>
              </div>
            )
          })}
        </div>
      )}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const { addToast, removeToast } = useContext(ToastContext)
  if (!addToast) throw new Error('useToast must be used within a ToastProvider')
  // Memoize the returned object so callers can safely include `toast` in
  // useEffect / useCallback dep arrays. addToast and removeToast are stable
  // (useCallback in the Provider), so this object is stable for the lifetime
  // of the Provider.
  return useMemo(() => ({
    success: (msg, dur) => addToast(msg, 'success', dur),
    error: (msg, dur) => addToast(msg, 'error', dur ?? 6000),
    warning: (msg, dur) => addToast(msg, 'warning', dur),
    info: (msg, dur) => addToast(msg, 'info', dur),
    loading: (msg) => addToast(msg, 'loading', 0), // persists until toast.remove(id)
    // Shows a toast with an action button — e.g. toast.action('Archived', 'Undo', () => unarchive(), 5000)
    action: (msg, actionLabel, actionFn, dur = 5000) =>
      addToast(msg, 'info', dur, { label: actionLabel, onClick: actionFn }),
    remove: removeToast,
  }), [addToast, removeToast])
}
